/**
 * Story 052 — Data & Privacy section.
 *
 * AC23: Download my data → POST /me/data-export-request → toast.
 * AC24: Delete my account → confirm → POST /me/deletion-request → toast.
 */
import { useState, type ReactElement } from "react";
import {
  Alert,
  Box,
  Button,
  Collapse,
  Stack,
  Typography,
} from "@mui/material";
import { SectionShell } from "./SectionShell.js";
import {
  useRequestDataExport,
  useRequestDeletion,
} from "../queries/index.js";
import { useToast } from "../ToastContext.js";

export function PrivacySection(): ReactElement {
  const exportReq = useRequestDataExport();
  const deleteReq = useRequestDeletion();
  const { toast } = useToast();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [exportMessage, setExportMessage] = useState<string | null>(null);
  const [deleteMessage, setDeleteMessage] = useState<string | null>(null);

  return (
    <SectionShell id="privacy" title="Privacy">
      <Stack spacing={3}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Download my data
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            GDPR Article 15 (right of access). Your tenant admin will email
            you the export.
          </Typography>
          <Button
            variant="outlined"
            onClick={() => {
              exportReq.mutate(undefined, {
                onSuccess: () => {
                  const msg =
                    "Request submitted — your tenant admin will email you the export within 30 days.";
                  setExportMessage(msg);
                  toast("Export request submitted");
                },
                onError: () => {
                  toast("Couldn't submit export request", "error");
                },
              });
            }}
          >
            Download my data
          </Button>
          {exportMessage ? (
            <Alert severity="info" sx={{ mt: 2 }} role="status">
              {exportMessage}
            </Alert>
          ) : null}
        </Box>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Delete my account
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            GDPR Article 17 (right to erasure). Submitting this request
            sends a deletion notice to your tenant administrator.
          </Typography>
          <Button
            variant="outlined"
            color="error"
            onClick={() => {
              setConfirmDelete(true);
            }}
          >
            Delete my account
          </Button>
          <Collapse in={confirmDelete}>
            <Alert
              severity="warning"
              sx={{ mt: 2 }}
              action={
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    onClick={() => {
                      setConfirmDelete(false);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    color="error"
                    onClick={() => {
                      deleteReq.mutate(undefined, {
                        onSuccess: () => {
                          const msg =
                            "Request submitted — your tenant admin will confirm and complete deletion.";
                          setDeleteMessage(msg);
                          setConfirmDelete(false);
                          toast("Deletion request submitted");
                        },
                        onError: () => {
                          toast("Couldn't submit deletion request", "error");
                        },
                      });
                    }}
                  >
                    Confirm
                  </Button>
                </Stack>
              }
            >
              This will request deletion of your account. Confirm to submit
              the request.
            </Alert>
          </Collapse>
          {deleteMessage ? (
            <Alert severity="info" sx={{ mt: 2 }} role="status">
              {deleteMessage}
            </Alert>
          ) : null}
        </Box>
      </Stack>
    </SectionShell>
  );
}
